/**
 * URL Shortener Utility - PolicyTrackr Owned Short Links
 * 
 * This module provides URL shortening functionality using PolicyTrackr's own
 * Firebase-backed short link system. No third-party services are used.
 * 
 * Short links are in the format: https://policytrackr.app/d/{shortId}
 * Links auto-expire after 30 minutes by default.
 */

import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';

// Generate a random URL-safe short ID (6-8 characters)
const generateShortId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

/**
 * Create a short URL for a given long URL
 * 
 * @param {string} longUrl - The URL to shorten (Firebase Storage or Google Drive URL)
 * @param {number} expirationMinutes - Link expiration in minutes (default: 30)
 * @returns {Promise<string>} The short URL in format https://policytrackr.app/d/{shortId}
 */
export const createShortUrl = async (longUrl, expirationMinutes = 30) => {
  console.log('Creating PolicyTrackr short URL for:', longUrl);

  // Validate input
  if (!longUrl || typeof longUrl !== 'string') {
    console.error('Invalid URL provided to createShortUrl');
    return longUrl;
  }

  try {
    let shortId;
    let attempts = 0;
    const maxAttempts = 5;

    // Generate a unique short ID
    do {
      shortId = generateShortId();
      const docRef = doc(db, 'short_links', shortId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        break; // ID is unique
      }
      attempts++;
    } while (attempts < maxAttempts);

    if (attempts >= maxAttempts) {
      console.error('Unable to generate unique short ID after', maxAttempts, 'attempts');
      return longUrl; // Fallback to original URL
    }

    // Calculate expiration time
    const now = new Date();
    const expiresAt = new Date(now.getTime() + expirationMinutes * 60 * 1000);

    // Store the mapping in Firestore
    const docRef = doc(db, 'short_links', shortId);
    await setDoc(docRef, {
      targetUrl: longUrl,
      createdAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    });

    const shortUrl = `https://policytrackr.app/d/${shortId}`;
    console.log('✅ Short URL created:', shortUrl);
    console.log('   Expires at:', expiresAt.toLocaleString());

    return shortUrl;
  } catch (error) {
    console.error('Error creating short link:', error);
    // Fallback to original URL if short link creation fails
    return longUrl;
  }
};

/**
 * Create WhatsApp message with URL shortening
 * 
 * @param {string} pdfUrl - The PDF URL to include in the message
 * @param {string} agentName - The agent's name for personalization
 * @param {string} policyId - The policy ID (for logging)
 * @param {boolean} isSharePdf - True if sharing PDF, false if renewal notification
 * @param {number|null} premiumAmount - Premium amount for renewal messages
 * @param {number|null} balanceAmount - Balance amount for renewal messages
 * @returns {Promise<string>} The formatted WhatsApp message with short URL
 */
export const createWhatsAppMessage = async (pdfUrl, agentName, policyId, isSharePdf = false, premiumAmount = null, balanceAmount = null) => {
  console.log('Creating WhatsApp message for PDF:', pdfUrl);

  // Validate PDF URL
  if (!pdfUrl || typeof pdfUrl !== 'string') {
    throw new Error('Invalid PDF URL provided');
  }

  // Create message helper function
  const createMessage = (url) => {
    if (isSharePdf) {
      return `You can view and download your policy here: ${url}`;
    } else {
      const premiumText = premiumAmount ? `Premium - ₹${Number(premiumAmount).toFixed(2)}` : '';
      const balanceText = balanceAmount !== null ? `Balance - ₹${Number(balanceAmount).toFixed(2)}` : '';
      const amountInfo = premiumText && balanceText ? `\n${premiumText}, ${balanceText}` : '';

      return agentName
        ? `Your policy has been renewed successfully by ${agentName} ✅${amountInfo}\nTap below to download your renewed policy 📄:\n${url}`
        : `Your policy has been renewed successfully ✅${amountInfo}\nTap below to download your renewed policy 📄:\n${url}`;
    }
  };

  // Skip URL shortening for URLs that can't be accessed externally
  if (pdfUrl.includes('console.firebase.google.com') ||
    pdfUrl.includes('localhost') ||
    pdfUrl.includes('127.0.0.1') ||
    pdfUrl.includes('192.168.') ||
    pdfUrl.includes('10.0.') ||
    pdfUrl.includes('172.16.') ||
    pdfUrl.includes('172.17.') ||
    pdfUrl.includes('172.18.') ||
    pdfUrl.includes('172.19.') ||
    pdfUrl.includes('172.20.') ||
    pdfUrl.includes('172.21.') ||
    pdfUrl.includes('172.22.') ||
    pdfUrl.includes('172.23.') ||
    pdfUrl.includes('172.24.') ||
    pdfUrl.includes('172.25.') ||
    pdfUrl.includes('172.26.') ||
    pdfUrl.includes('172.27.') ||
    pdfUrl.includes('172.28.') ||
    pdfUrl.includes('172.29.') ||
    pdfUrl.includes('172.30.') ||
    pdfUrl.includes('172.31.')) {
    console.log('Skipping URL shortening for private/local IP address:', pdfUrl);
    return createMessage(pdfUrl);
  }

  // Create short URL using PolicyTrackr's own system
  try {
    console.log('Creating PolicyTrackr short URL...');

    const shortUrl = await Promise.race([
      createShortUrl(pdfUrl, 30), // 30 minute expiration
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('URL shortening timeout after 10 seconds')), 10000)
      )
    ]);

    console.log('✅ URL shortening SUCCESS! Short URL:', shortUrl);
    console.log('WhatsApp message created successfully with short URL');
    return createMessage(shortUrl);
  } catch (error) {
    console.log('❌ URL shortening FAILED:', error.message);
    console.log('Using original URL as fallback');
    return createMessage(pdfUrl);
  }
};
